import React, { useEffect, useState, useCallback } from 'react';
import { todayIso, fmtTime } from '../utils/dateUtils';

const OUTCOME_STYLE = {
  CREATED:           { bg: '#dcfce7', color: '#166534' },
  CANCELLED:         { bg: '#fee2e2', color: '#991b1b' },
  MODIFIED:          { bg: '#dbeafe', color: '#1e40af' },
  DUPLICATE:         { bg: '#fef9c3', color: '#854d0e' },
  ALREADY_CANCELLED: { bg: '#f3f4f6', color: '#6b7280' },
  NOT_FOUND:         { bg: '#fef3c7', color: '#92400e' },
  ERROR:             { bg: '#fee2e2', color: '#991b1b' },
  UNKNOWN_STATUS:    { bg: '#f3f4f6', color: '#6b7280' },
};

const STATUS_STYLE = {
  Booked:    { bg: '#dbeafe', color: '#1e40af' },
  Cancelled: { bg: '#fee2e2', color: '#991b1b' },
  Modified:  { bg: '#fef3c7', color: '#92400e' },
};

function Badge({ value, styles }) {
  const s = styles[value] || { bg: '#f3f4f6', color: 'var(--theme-text)' };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 700 }}>
      {value || '—'}
    </span>
  );
}

const fmtAmount = (v) => {
  if (v == null || v === '') return '—';
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function PmsCmBookingLog({ token, selectedProperty }) {
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [statusFilter, setStatusFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      let url = `/api/v1/channel-manager/booking-log?property_id=${selectedProperty.id}&from_date=${fromDate}&to_date=${toDate}&limit=200`;
      if (statusFilter) url += `&booking_status=${encodeURIComponent(statusFilter)}`;
      if (outcomeFilter) url += `&outcome=${encodeURIComponent(outcomeFilter)}`;
      const res = await fetch(url, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      setRows(data.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load OTA booking log');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, fromDate, toDate, statusFilter, outcomeFilter, headers]);

  useEffect(() => { load(); }, [load]);

  const summary = {
    total: rows.length,
    created: rows.filter((r) => r.outcome === 'CREATED').length,
    cancelled: rows.filter((r) => r.outcome === 'CANCELLED').length,
    modified: rows.filter((r) => r.outcome === 'MODIFIED').length,
    errors: rows.filter((r) => ['ERROR', 'NOT_FOUND', 'UNKNOWN_STATUS'].includes(r.outcome)).length,
    duplicates: rows.filter((r) => ['DUPLICATE', 'ALREADY_CANCELLED'].includes(r.outcome)).length,
  };

  return (
    <div className="page-shell">
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.5rem' }}>
        Front Office <span style={{ color: '#cbd5e0' }}>&rsaquo;</span> <span style={{ color: 'var(--theme-primary)', fontWeight: 600 }}>OTA Booking Log</span>
      </div>
      <h3 style={{ margin: '0 0 0.75rem', color: 'var(--theme-primary)' }}>OTA Booking Log (Inbound from EaseRoom)</h3>

      {!selectedProperty?.id && (
        <div className="error-banner">Please select a property first.</div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--theme-text)' }}>From:</label>
        <input type="date" value={fromDate} onChange={(e) => { const v = e.target.value; setFromDate(v); if (toDate && v && toDate < v) setToDate(v); }}
          style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem' }} />
        <label style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--theme-text)' }}>To:</label>
        <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => { const v = e.target.value; setToDate(v); if (fromDate && v && fromDate > v) setFromDate(v); }}
          style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem' }} />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem' }}>
          <option value="">All Statuses</option>
          {['Booked', 'Cancelled', 'Modified'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)}
          style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem' }}>
          <option value="">All Outcomes</option>
          {['CREATED', 'CANCELLED', 'MODIFIED', 'DUPLICATE', 'ALREADY_CANCELLED', 'NOT_FOUND', 'ERROR'].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <button onClick={load} disabled={loading}
          style={{ padding: '0.4rem 1rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        <button onClick={() => { const t = todayIso(); setFromDate(t); setToDate(t); }}
          style={{ padding: '0.4rem 0.9rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--theme-text)' }}>
          Today
        </button>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: '0.8rem' }}>{error}</div>}

      {/* Summary cards */}
      {rows.length > 0 && (
        <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Total Received', value: summary.total, bg: '#eff6ff', color: '#1e40af' },
            { label: 'New Bookings', value: summary.created, bg: '#dcfce7', color: '#166534' },
            { label: 'Cancelled', value: summary.cancelled, bg: '#fee2e2', color: '#991b1b' },
            { label: 'Modified', value: summary.modified, bg: '#dbeafe', color: '#1e40af' },
            { label: 'Duplicates', value: summary.duplicates, bg: '#fef9c3', color: '#854d0e' },
            { label: 'Errors', value: summary.errors, bg: '#fef3c7', color: '#92400e' },
          ].map((c) => (
            <div key={c.label} style={{ background: c.bg, color: c.color, borderRadius: '8px', padding: '0.5rem 1rem', fontSize: '0.82rem', fontWeight: 600, minWidth: '90px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{c.value}</div>
              <div style={{ fontSize: '0.72rem', marginTop: '1px' }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ color: '#6b7280', padding: '2rem', textAlign: 'center' }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={{ color: '#6b7280', padding: '2rem', textAlign: 'center', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          No OTA bookings received for the selected period.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--theme-border-light)' }}>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)', whiteSpace: 'nowrap' }}>Time</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>OTA Booking ID</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>Source / OTA</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>Guest</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>Status</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)', whiteSpace: 'nowrap' }}>Stay Dates</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right', borderBottom: '2px solid var(--theme-border)' }}>Amount</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>Pay@Hotel</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>PMS Reservation</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>Meal Plan</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>Outcome</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>Error</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const guestName = String(r.guest_name || '').trim();
                return (
                <React.Fragment key={r.log_id}>
                  <tr
                    onClick={() => setExpandedRow((prev) => prev === r.log_id ? null : r.log_id)}
                    style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer', background: expandedRow === r.log_id ? 'var(--theme-bg-light)' : undefined }}
                  >
                    <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap' }}>{fmtTime(r.received_at)}</td>
                    <td style={{ padding: '0.45rem 0.6rem', fontFamily: 'monospace', fontSize: '0.78rem', color: '#1e40af' }}>{r.booking_id || r.ota_booking_id || '—'}</td>
                    <td style={{ padding: '0.45rem 0.6rem' }}>
                      {r.ota_source ? (
                        <span style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: '4px', padding: '1px 6px', fontSize: '0.72rem', fontWeight: 700 }}>
                          {r.ota_source}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {guestName || '—'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem' }}><Badge value={r.booking_status} styles={STATUS_STYLE} /></td>
                    <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                      {r.checkin_date ? `${r.checkin_date} — ${r.checkout_date || ''}` : '—'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {fmtAmount(r.total_amount)}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem' }}>
                      {r.pay_at_hotel === 'Yes' ? (
                        <span style={{ background: '#dcfce7', color: '#166534', borderRadius: '4px', padding: '1px 6px', fontSize: '0.72rem', fontWeight: 700 }}>Yes</span>
                      ) : r.pay_at_hotel === 'No' ? (
                        <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: '4px', padding: '1px 6px', fontSize: '0.72rem', fontWeight: 700 }}>Pre-paid</span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', fontWeight: 600, color: r.reservation_id ? '#1e40af' : '#9ca3af' }}>
                      {r.reservation_id ? `RES-${r.reservation_id}` : '—'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', color: '#6b7280' }}>
                      {r.meal_plan_label || r.meal_plan_code || '—'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem' }}><Badge value={r.outcome} styles={OUTCOME_STYLE} /></td>
                    <td style={{ padding: '0.45rem 0.6rem', color: r.error_message ? 'var(--theme-danger)' : '#6b7280', fontSize: '0.78rem', maxWidth: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {r.error_message ? (r.error_message.length > 60 ? r.error_message.slice(0, 60) + '...' : r.error_message) : ''}
                    </td>
                  </tr>

                  {/* Expanded detail */}
                  {expandedRow === r.log_id && (
                    <tr>
                      <td colSpan={12} style={{ padding: '0.7rem 1rem', background: 'var(--theme-bg-light)', borderBottom: '2px solid var(--theme-border)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.75rem', marginBottom: '0.3rem' }}>Guest & Booking Details</div>
                            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem', fontSize: '0.78rem' }}>
                              <div><b>Guest:</b> {guestName || '—'}</div>
                              <div><b>Email:</b> {r.guest_email || '—'}</div>
                              <div><b>Phone:</b> {r.guest_phone || '—'}</div>
                              <div><b>OTA Booking ID:</b> {r.booking_id || r.ota_booking_id || '—'}</div>
                              <div><b>Source / OTA:</b> {r.ota_source || '—'}</div>
                              <div><b>Status:</b> {r.booking_status || '—'} &nbsp; <b>Rooms:</b> {r.no_of_rooms || '—'}</div>
                              <div><b>Check-in:</b> {r.checkin_date || '—'} &nbsp; <b>Check-out:</b> {r.checkout_date || '—'}</div>
                              <div><b>Net Rate:</b> {fmtAmount(r.net_rate)} &nbsp; <b>Tax:</b> {fmtAmount(r.tax_amount)} &nbsp; <b>Booking Amount:</b> {fmtAmount(r.total_amount)}</div>
                              <div><b>Pay@Hotel:</b> {r.pay_at_hotel || '—'} &nbsp; <b>Channel:</b> {r.ota_channel_name || '—'}</div>
                              <div><b>EaseRoom Hotel ID:</b> {r.gi_hotel_id || '—'}</div>
                            </div>
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.75rem', marginBottom: '0.3rem' }}>PMS Processing</div>
                            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem', fontSize: '0.78rem' }}>
                              <div><b>PMS Reservation:</b> {r.reservation_id ? `RES-${r.reservation_id}` : '—'}</div>
                              <div><b>Meal Plan:</b> {r.meal_plan_label || r.meal_plan_code || '—'}</div>
                              <div><b>Outcome:</b> {r.outcome || '—'}</div>
                              {r.error_message && <div style={{ color: 'var(--theme-danger)', marginTop: '0.3rem' }}><b>Error:</b> {r.error_message}</div>}
                            </div>
                          </div>
                        </div>
                        <div style={{ marginTop: '0.7rem' }}>
                          <div style={{ fontWeight: 700, fontSize: '0.75rem', marginBottom: '0.3rem' }}>Raw Webhook Payload</div>
                          <pre style={{
                            background: '#0f172a', color: '#e2e8f0', borderRadius: '6px', padding: '0.7rem',
                            fontSize: '0.74rem', overflowX: 'auto', maxHeight: '340px', overflowY: 'auto', margin: 0,
                          }}>
                            {r.raw_payload ? JSON.stringify(r.raw_payload, null, 2) : 'No payload captured'}
                          </pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PmsCmBookingLog;
